import React, {
  forwardRef,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import TProductDeutscheTelekomBrandClaim from "@/icons/TProduct/DeutscheTelekomBrandClaim";
import TProductDeutscheTelekomBrandLogo from "@/icons/TProduct/DeutscheTelekomBrandLogo";
import ActionCircleCloseDefault from "@/icons/Action/CircleClose/Default";
import ActionShoppingCartDefault from "@/icons/Action/ShoppingCart/Default";
import ActionSearchDefault from "@/icons/Action/Search/Default";
import UserFileUserDefault from "@/icons/UserFile/User/Default";
import ActionMenuDefault from "@/icons/Action/Menu/Default";
import ActionSuccessDefault from "@/icons/Action/Success/Default";

import { BrandBar } from "../BrandBar";
import { TextLink } from "../TextLink";
import { LinkGroup } from "../LinkGroup";
import { Icon } from "../Icon";
import { MenuButton } from "../MenuButton";
import { Flex } from "../Flex";
import { Flyout } from "../Flyout";
import { Tabs } from "../Tabs";
import { PagedMenu } from "../PagedMenu";
import ForwardControl from "../PagedMenu/ForwardControl";
import BackwardControl from "../PagedMenu/BackwardControl";
import MenuEntry from "../PagedMenu/PagedMenuEntry";

import { useScrolledTop } from "../hooks/useScrolledTop";
import { useMediaQuery, mediaQueries } from "../hooks/useMediaQuery";

import LoginBox from "./LoginBox";

import styles from "./GlobalHeader.scss";

export const GlobalHeader = forwardRef(
  (
    {
      checkout,
      loggedIn,
      username,
      articleCount,
      navGroups,
      zIndex,
      customClass,
    },
    ref
  ) => {
    const scrolledTop = useScrolledTop();
    const isViewportS = useMediaQuery(mediaQueries.s);
    const isViewportL = useMediaQuery(mediaQueries.l);
    const menuButtonAlignment =
      isViewportS || isViewportL ? "vertical" : "horizontal";
    const pagedMenuRef = useRef();
    const navBarRef = useRef();
    const navBarContentRef = useRef();
    const [activeFlyout, setActiveFlyout] = useState("");
    const [scrollbarWidth, setScrollbarWidth] = useState(0);

    const handleMouseLeave = useCallback((e) => {
      if (
        e.relatedTarget === navBarRef.current ||
        e.relatedTarget === navBarContentRef.current
      ) {
        setActiveFlyout("");
      }
    }, []);

    function handleClick(event, hasFlyout = true) {
      if (hasFlyout) {
        const id = event.currentTarget.getAttribute("data-flyout-target");
        if (activeFlyout !== id) {
          event.preventDefault();
          setActiveFlyout(id);
        }
      } else {
        setActiveFlyout("");
      }
    }

    function openFlyout(event) {
      const id = event.currentTarget.getAttribute("data-flyout-target");

      if (id !== activeFlyout) setActiveFlyout(id);
    }

    function toggleFlyout(event) {
      const id = event.target.getAttribute("data-flyout-target");
      if (activeFlyout !== id) {
        setActiveFlyout(id);
      } else {
        setActiveFlyout("");
      }
    }

    useEffect(() => {
      setActiveFlyout("");
    }, [checkout]);

    useEffect(() => {
      // used to account for scrollbar width for mobile menu
      if (pagedMenuRef.current) {
        setScrollbarWidth(
          pagedMenuRef.current.offsetWidth - pagedMenuRef.current.clientWidth
        );
      }
    }, [activeFlyout]);

    return (
      <header
        ref={ref}
        className={cx(styles.globalHeader, customClass, {
          [styles.globalHeaderMinimized]: !scrolledTop,
        })}
        style={zIndex ? { zIndex } : null}
      >
        <div
          className={cx(styles.globalHeaderFixate, {
            [styles.overlay]:
              activeFlyout && activeFlyout !== "menu" && !checkout,
          })}
        >
          <BrandBar
            logoHref="http://www.telekom.de"
            logoAlt="Telekom Logo"
            claim={<Icon icon={TProductDeutscheTelekomBrandClaim} />}
            claimAlt="Telekom Claim"
            expanded={checkout}
            minimized={!scrolledTop}
            onMouseEnter={() => setActiveFlyout("")}
          >
            {!checkout && (
              <>
                <BrandBar.Link href="https://www.telekom.de/start" active>
                  Privatkunden
                </BrandBar.Link>
                <BrandBar.Link href="https://geschaeftskunden.telekom.de/">
                  Geschäftskunden
                </BrandBar.Link>
                <BrandBar.Divider />
              </>
            )}
            <BrandBar.Link href="https://www.telekom.de/kontakt">
              Telekom Shops
            </BrandBar.Link>
            <BrandBar.Link href="https://www.telekom.de/start/telekom-shops">
              Kontakt
            </BrandBar.Link>
          </BrandBar>
          {!checkout && (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              customClass={cx(styles.navigationalBar, {
                [styles.navigationalBarBorderBottom]: activeFlyout !== "",
              })}
              ref={navBarRef}
            >
              <Flex
                justifyContent="space-between"
                alignItems="center"
                customClass={cx(styles.navigationalBarContent)}
                ref={navBarContentRef}
              >
                <a
                  className={styles.brandLink}
                  aria-label="Telekom Brand Claim"
                  href="http://www.telekom.de"
                >
                  <Icon
                    icon={TProductDeutscheTelekomBrandLogo}
                    color={Icon.Color.magenta}
                    size={Icon.Size.parent}
                    wrapperProps={{ className: styles.brandLogo }}
                  />
                </a>
                <span className={styles.navLinkGroup}>
                  {navGroups &&
                    Object.entries(navGroups).map(([id, navGroup]) => {
                      if (navGroup.loginBox) return "";
                      return (
                        <TextLink
                          variants={[
                            TextLink.Variant.blackLarge,
                            styles.navLink,
                          ]}
                          href={navGroup.href ? navGroup.href : `#${id}`}
                          data-flyout-target={id}
                          onClick={
                            !navGroup.linkGroups ? undefined : handleClick
                          }
                          onMouseEnter={
                            !navGroup.linkGroups ? undefined : handleClick
                          }
                          onMouseLeave={handleMouseLeave}
                          key={id}
                        >
                          {navGroup.title}
                        </TextLink>
                      );
                    })}
                </span>
                <span className={styles.menuButtonGroup}>
                  <MenuButton
                    align={menuButtonAlignment}
                    icon={ActionSearchDefault}
                    customClass={styles.menuButton}
                  >
                    Suche
                  </MenuButton>

                  <MenuButton
                    align={menuButtonAlignment}
                    icon={ActionShoppingCartDefault}
                    iconProps={{ customClass: styles.menuButtonIcon }}
                    customClass={styles.menuButton}
                    tag="a"
                    alt="Warenkorb"
                    href="https://www.telekom.de/unterwegs/warenkorb"
                    indicatorContent={articleCount > 0 ? articleCount : null}
                    onMouseEnter={() => setActiveFlyout("")}
                  >
                    Warenkorb
                  </MenuButton>

                  <MenuButton
                    align={menuButtonAlignment}
                    icon={UserFileUserDefault}
                    customClass={styles.menuButtonLogin}
                    onMouseEnter={openFlyout}
                    onClick={toggleFlyout}
                    data-flyout-target="login"
                    onMouseLeave={handleMouseLeave}
                    indicatorContent={
                      loggedIn && (
                        <Icon
                          icon={ActionSuccessDefault}
                          size={Icon.Size.xxsmall}
                          wrapperProps={{ style: { width: 12 } }}
                        />
                      )
                    }
                  >
                    {loggedIn ? "Logout" : "Login"}
                  </MenuButton>

                  <MenuButton
                    align={menuButtonAlignment}
                    icon={
                      activeFlyout === "menu"
                        ? ActionCircleCloseDefault
                        : ActionMenuDefault
                    }
                    customClass={styles.menuButtonMenu}
                    onMouseEnter={openFlyout}
                    onClick={toggleFlyout}
                    data-flyout-target="menu"
                  >
                    Menu
                  </MenuButton>
                </span>
              </Flex>
            </Flex>
          )}

          {/* Flyouts */}
          {navGroups &&
            Object.entries(navGroups).map(([id, navGroup]) => (
              <Flyout
                onMouseLeave={() => setActiveFlyout("")}
                isOpen={activeFlyout === id}
                key={id}
                className={cx({
                  [styles["flyout-login-box"]]: navGroup.loginBox,
                })}
              >
                <div className={styles.tile}>
                  <Flex
                    justifyContent={navGroup.loginBox && "space-between"}
                    customClass={styles.flyout}
                  >
                    {activeFlyout !== "login" && (
                      <div
                        className={navGroup.loginBox && styles.loginLinkGroups}
                      >
                        <Flex
                          customClass={styles.navFlyout}
                          alignItems={navGroup.loginBox && "flex-start"}
                        >
                          {navGroup.linkGroups &&
                            Object.entries(navGroup.linkGroups).map(
                              ([linkGroupId, linkGroup]) => {
                                const {
                                  text: headerText,
                                  ...headerLinkProps
                                } = linkGroup.header;
                                return (
                                  <LinkGroup
                                    className={styles.linkGroup}
                                    key={linkGroupId}
                                  >
                                    <LinkGroup.Header>
                                      <TextLink
                                        variants={TextLink.Variant.blackMedium}
                                        {...headerLinkProps}
                                      >
                                        {headerText}
                                      </TextLink>
                                    </LinkGroup.Header>
                                    <LinkGroup.Body>
                                      {linkGroup.links &&
                                        linkGroup.links.map((link) => {
                                          const {
                                            text: linkText,
                                            ...linkProps
                                          } = link;
                                          return (
                                            <LinkGroup.Item key={link.text}>
                                              <TextLink
                                                variants={
                                                  TextLink.Variant.blackSmall
                                                }
                                                {...linkProps}
                                              >
                                                {linkText}
                                              </TextLink>
                                            </LinkGroup.Item>
                                          );
                                        })}
                                    </LinkGroup.Body>
                                  </LinkGroup>
                                );
                              }
                            )}
                        </Flex>
                      </div>
                    )}
                    {navGroup.loginBox && (
                      <LoginBox
                        loggedIn={loggedIn}
                        username={username}
                        primaryLinks={navGroup.loginBox.primaryLinks}
                        secondaryLinks={navGroup.loginBox.secondaryLinks}
                      />
                    )}
                  </Flex>
                  <Flex justifyContent="center">
                    <MenuButton
                      align="vertical"
                      icon={ActionCircleCloseDefault}
                      customClass={cx(styles.closeButton, {
                        [styles.closeButtonLogin]: activeFlyout === "login",
                      })}
                      onClick={() => setActiveFlyout("")}
                      equalSize
                    >
                      {activeFlyout === "login"
                        ? "Schließen"
                        : "Menü schließen"}
                    </MenuButton>
                  </Flex>
                </div>
              </Flyout>
            ))}

          {/* Mobile Menu */}
          <Flyout
            isOpen={activeFlyout === "menu"}
            className={styles.mobileMenu}
          >
            <div className={styles.menuTile}>
              <div className={styles.content}>
                <Tabs scheme={styles.tabsWrapper} style={{ display: "flex" }}>
                  <Tabs.List>
                    {/* Using style since className overwrites tabs styles */}
                    <Tabs.Tab style={{ width: "50vw", padding: "16px 0" }}>
                      <span className={styles.tabText}>Privatkunden</span>
                    </Tabs.Tab>
                    <Tabs.Tab
                      style={{ width: "50vw", padding: "16px 0" }}
                      onClick={() =>
                        window.open("https://geschaeftskunden.telekom.de/")
                      }
                    >
                      <span className={styles.tabText}>Geschäftskunden</span>
                    </Tabs.Tab>
                  </Tabs.List>
                </Tabs>
                <div
                  ref={pagedMenuRef}
                  className={styles.pagedMenuContainer}
                  style={{ marginRight: -scrollbarWidth }}
                >
                  <PagedMenu initialPage="root">
                    {/* First Layer */}
                    <PagedMenu.Page id="root">
                      {navGroups &&
                        Object.entries(navGroups).map(([id, navGroup]) => {
                          if (navGroup.loginBox) return "";
                          if (!navGroup.linkGroups) {
                            return (
                              <MenuEntry
                                tag="a"
                                href={navGroup.href}
                                variant="large"
                                key={id}
                              >
                                {navGroup.title}
                              </MenuEntry>
                            );
                          }
                          return (
                            <ForwardControl targetPage={id} key={id}>
                              {navGroup.title}
                            </ForwardControl>
                          );
                        })}
                      <div className={styles.linkContainer}>
                        <TextLink
                          customClass={styles.link}
                          href="https://www.telekom.de/kontakt"
                        >
                          Kontakt
                        </TextLink>
                        <TextLink
                          customClass={styles.link}
                          href="https://www.telekom.de/start/telekom-shops"
                        >
                          Telekom Shops
                        </TextLink>
                      </div>
                    </PagedMenu.Page>

                    {/* Second Layer */}
                    {navGroups &&
                      Object.entries(navGroups).map(([groupId, navGroup]) => {
                        if (navGroup.loginBox || !navGroup.linkGroups)
                          return "";
                        return (
                          <PagedMenu.Page id={groupId} key={groupId}>
                            <BackwardControl targetPage="root">
                              {navGroup.title}
                            </BackwardControl>
                            <MenuEntry tag="a" small href={navGroup.href}>
                              Übersicht {navGroup.title}
                            </MenuEntry>
                            {Object.entries(navGroup.linkGroups).map(
                              ([linkGroupId, linkGroup]) => (
                                <ForwardControl
                                  small
                                  targetPage={`${groupId}-${linkGroupId}`}
                                  key={linkGroup.header.text}
                                >
                                  {linkGroup.header.text}
                                </ForwardControl>
                              )
                            )}
                          </PagedMenu.Page>
                        );
                      })}

                    {/* Third Layer */}
                    {navGroups &&
                      Object.entries(navGroups).map(([groupId, navGroup]) => {
                        if (navGroup.loginBox || !navGroup.linkGroups)
                          return "";
                        return Object.entries(navGroup.linkGroups).map(
                          ([linkGroupId, linkGroup]) => (
                            <PagedMenu.Page
                              id={`${groupId}-${linkGroupId}`}
                              key={linkGroupId}
                            >
                              <BackwardControl targetPage={groupId}>
                                {linkGroup.header.text}
                              </BackwardControl>
                              <MenuEntry
                                tag="a"
                                small
                                href={linkGroup.header.href}
                              >
                                Übersicht {linkGroup.header.text}
                              </MenuEntry>
                              {linkGroup.links &&
                                linkGroup.links.map((link) => (
                                  <MenuEntry
                                    tag="a"
                                    small
                                    href={link.href}
                                    key={link.text}
                                  >
                                    {link.text}
                                  </MenuEntry>
                                ))}
                            </PagedMenu.Page>
                          )
                        );
                      })}
                  </PagedMenu>
                </div>
              </div>
            </div>
          </Flyout>
        </div>
      </header>
    );
  }
);

GlobalHeader.displayName = "GlobalHeader";

GlobalHeader.propTypes = {
  checkout: PropTypes.bool.isRequired,
  loggedIn: PropTypes.bool.isRequired,
  username: PropTypes.string,
  articleCount: PropTypes.number.isRequired,
  navGroups: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      href: PropTypes.string,
      loginBox: PropTypes.boolean,
      linkGroups: PropTypes.objectOf(
        PropTypes.shape({
          header: PropTypes.shape({
            text: PropTypes.string.isRequired,
            href: PropTypes.string,
          }).isRequired,
          links: PropTypes.arrayOf(
            PropTypes.shape({
              text: PropTypes.string.isRequired,
              href: PropTypes.string,
            })
          ),
        })
      ),
    })
  ).isRequired,
  zIndex: PropTypes.number,
  customClass: PropTypes.string,
};

GlobalHeader.defaultProps = {
  username: null,
  zIndex: undefined,
  customClass: undefined,
};

export default GlobalHeader;
